<template>
  <div class="customer-packaging-rules-panel">
    <p class="customer-packaging-rules-panel__intro">
      按<strong>客户</strong>与<strong>订单类型</strong>预设派工包装要求（袋型 / 是否称重 / 是否量体积）。
      生成派工单时会自动带入对应明细；未单独配置订单类型时可设「全部订单类型」作为该客户通用规则。
    </p>

    <div class="customer-packaging-rules-panel__toolbar">
      <el-select
        v-model="filterCustomerId"
        placeholder="筛选客户"
        clearable
        filterable
        style="width: 220px"
        @change="loadRules"
      >
        <el-option
          v-for="c in customerList"
          :key="c.customer_id"
          :label="c.customer_name"
          :value="c.customer_id"
        />
      </el-select>
      <el-select
        v-model="filterOrderTypeId"
        placeholder="订单类型"
        clearable
        filterable
        style="width: 180px"
        @change="loadRules"
      >
        <el-option
          v-for="ot in orderTypeList"
          :key="ot.order_type_id"
          :label="ot.type_name_cn"
          :value="ot.order_type_id"
        />
      </el-select>
      <el-checkbox v-model="filterIncludeInactive" @change="loadRules">含停用</el-checkbox>
      <el-button type="primary" :disabled="!canEdit" @click="openCreate">
        <el-icon><Plus /></el-icon> 新增规则
      </el-button>
      <el-button @click="loadRules">
        <el-icon><Refresh /></el-icon> 刷新
      </el-button>
    </div>

    <el-table v-loading="loading" :data="rules" border stripe size="small">
      <el-table-column prop="customer_name" label="客户" min-width="140" show-overflow-tooltip />
      <el-table-column prop="order_type_scope_label" label="订单类型" width="140" show-overflow-tooltip />
      <el-table-column label="袋型" width="100">
        <template #default="{ row }">{{ row.packaging_type_label || '未指定' }}</template>
      </el-table-column>
      <el-table-column label="称重" width="72" align="center">
        <template #default="{ row }">{{ row.packaging_require_weigh ? '是' : '否' }}</template>
      </el-table-column>
      <el-table-column label="量体积" width="72" align="center">
        <template #default="{ row }">{{ row.packaging_require_volume ? '是' : '否' }}</template>
      </el-table-column>
      <el-table-column prop="is_active" label="启用" width="72" align="center">
        <template #default="{ row }">
          <el-tag :type="row.is_active ? 'success' : 'info'" size="small">{{ row.is_active ? '是' : '否' }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="notes" label="说明" min-width="120" show-overflow-tooltip />
      <el-table-column label="操作" width="150" fixed="right">
        <template #default="{ row }">
          <el-button size="small" :disabled="!canEdit" @click="openEdit(row)">编辑</el-button>
          <el-button size="small" type="danger" :disabled="!canEdit" @click="removeRule(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog
      v-model="formVisible"
      :title="formMode === 'create' ? '新增客户包装要求' : '编辑客户包装要求'"
      width="560px"
      destroy-on-close
      @closed="resetForm"
    >
      <el-form :model="form" label-width="100px">
        <el-form-item label="客户" required>
          <el-select
            v-model="form.customer_id"
            placeholder="选择客户"
            filterable
            style="width: 100%"
            :disabled="formMode === 'edit'"
          >
            <el-option
              v-for="c in customerList"
              :key="c.customer_id"
              :label="c.customer_name"
              :value="c.customer_id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="订单类型">
          <el-select
            v-model="form.order_type_id"
            placeholder="全部订单类型（通用）"
            clearable
            filterable
            style="width: 100%"
            :disabled="formMode === 'edit'"
          >
            <el-option
              v-for="ot in orderTypeList"
              :key="ot.order_type_id"
              :label="ot.type_name_cn"
              :value="ot.order_type_id"
            />
          </el-select>
          <p class="form-hint">不选表示该客户全部订单类型通用；选定后仅对该订单类型生效。</p>
        </el-form-item>
        <el-form-item label="袋型">
          <el-select v-model="form.packaging_type" placeholder="未指定" clearable style="width: 100%">
            <el-option
              v-for="opt in packagingTypeOptions"
              :key="opt.value"
              :label="opt.label"
              :value="opt.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="现场要求">
          <el-checkbox v-model="form.packaging_require_weigh">称重</el-checkbox>
          <el-checkbox v-model="form.packaging_require_volume">量体积</el-checkbox>
        </el-form-item>
        <el-form-item label="启用">
          <el-switch v-model="form.is_active" />
        </el-form-item>
        <el-form-item label="说明">
          <el-input v-model="form.notes" type="textarea" :rows="2" maxlength="500" show-word-limit />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="formVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="submitForm">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus, Refresh } from '@element-plus/icons-vue'
import { getActiveCustomers } from '@/api/customers'
import { getOrderTypes } from '@/api/orderTypes'
import {
  getCustomerPackagingRequirements,
  createCustomerPackagingRequirement,
  updateCustomerPackagingRequirement,
  deleteCustomerPackagingRequirement
} from '@/api/customerPackagingRequirements'
import { DISPATCH_PACKAGING_TYPE_OPTIONS } from '@/utils/customerPackagingRequirements'
import { useUserStore } from '@/store/user'
import { hasPermission } from '@/utils/permission'

const emit = defineEmits(['changed'])

const userStore = useUserStore()
userStore.initFromStorage()
const canEdit = computed(() => hasPermission(userStore.user, 'orders.dispatch.edit'))

const packagingTypeOptions = DISPATCH_PACKAGING_TYPE_OPTIONS

const loading = ref(false)
const saving = ref(false)
const rules = ref([])
const customerList = ref([])
const orderTypeList = ref([])
const filterCustomerId = ref(null)
const filterOrderTypeId = ref(null)
const filterIncludeInactive = ref(false)

const formVisible = ref(false)
const formMode = ref('create')
const form = reactive({
  requirement_id: null,
  customer_id: null,
  order_type_id: null,
  packaging_type: '',
  packaging_require_weigh: false,
  packaging_require_volume: false,
  is_active: true,
  notes: ''
})

async function loadCustomers() {
  const res = await getActiveCustomers({ page: 1, per_page: 2000 })
  if (res.success) customerList.value = res.data || []
}

async function loadOrderTypes() {
  const res = await getOrderTypes({ is_active: true })
  if (res.success) {
    orderTypeList.value = (res.data || []).slice().sort((a, b) =>
      String(a.type_name_cn || '').localeCompare(String(b.type_name_cn || ''), 'zh-CN')
    )
  }
}

async function loadRules() {
  loading.value = true
  try {
    const params = {}
    if (filterCustomerId.value) params.customer_id = filterCustomerId.value
    if (filterOrderTypeId.value) params.order_type_id = filterOrderTypeId.value
    if (filterIncludeInactive.value) params.include_inactive = '1'
    const res = await getCustomerPackagingRequirements(params)
    if (res.success) {
      rules.value = res.data || []
    } else {
      ElMessage.error(res.message || '加载失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '加载失败')
  } finally {
    loading.value = false
  }
}

function resetForm() {
  form.requirement_id = null
  form.customer_id = filterCustomerId.value || null
  form.order_type_id = filterOrderTypeId.value || null
  form.packaging_type = ''
  form.packaging_require_weigh = false
  form.packaging_require_volume = false
  form.is_active = true
  form.notes = ''
}

function openCreate() {
  formMode.value = 'create'
  resetForm()
  formVisible.value = true
}

function openEdit(row) {
  formMode.value = 'edit'
  form.requirement_id = row.requirement_id
  form.customer_id = row.customer_id
  form.order_type_id = row.order_type_id
  form.packaging_type = row.packaging_type || ''
  form.packaging_require_weigh = !!row.packaging_require_weigh
  form.packaging_require_volume = !!row.packaging_require_volume
  form.is_active = !!row.is_active
  form.notes = row.notes || ''
  formVisible.value = true
}

async function submitForm() {
  if (!form.customer_id) {
    ElMessage.warning('请选择客户')
    return
  }
  saving.value = true
  try {
    const payload = {
      customer_id: form.customer_id,
      order_type_id: form.order_type_id || null,
      packaging_type: form.packaging_type || null,
      packaging_require_weigh: form.packaging_require_weigh,
      packaging_require_volume: form.packaging_require_volume,
      is_active: form.is_active,
      notes: form.notes
    }
    const res =
      formMode.value === 'create'
        ? await createCustomerPackagingRequirement(payload)
        : await updateCustomerPackagingRequirement(form.requirement_id, payload)
    if (res.success) {
      ElMessage.success(res.message || '保存成功')
      formVisible.value = false
      await loadRules()
      emit('changed')
    } else {
      ElMessage.error(res.message || '保存失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '保存失败')
  } finally {
    saving.value = false
  }
}

async function removeRule(row) {
  try {
    await ElMessageBox.confirm(
      `确定删除客户「${row.customer_name}」的${row.order_type_scope_label}包装要求吗？`,
      '删除确认',
      { type: 'warning' }
    )
    const res = await deleteCustomerPackagingRequirement(row.requirement_id)
    if (res.success) {
      ElMessage.success('已删除')
      await loadRules()
      emit('changed')
    } else {
      ElMessage.error(res.message || '删除失败')
    }
  } catch (e) {
    if (e !== 'cancel') ElMessage.error(e.response?.data?.message || e.message || '删除失败')
  }
}

onMounted(async () => {
  await Promise.all([loadCustomers(), loadOrderTypes()])
  await loadRules()
})

defineExpose({ loadRules })
</script>

<style scoped>
.customer-packaging-rules-panel__intro {
  margin: 0 0 12px;
  font-size: 13px;
  line-height: 1.5;
  color: var(--el-text-color-regular);
}

.customer-packaging-rules-panel__toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  margin-bottom: 12px;
}

.form-hint {
  margin: 6px 0 0;
  font-size: 12px;
  color: var(--el-text-color-secondary);
  line-height: 1.4;
}
</style>
